import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../Experience'

/**
 * SceneDirector — スクロール（HTML層）と3Dシーンをつなぐ演出監督。
 *
 * HTML層が発火する2つの DOM CustomEvent を購読する:
 *   - 'sectionChanged'  { section }                      … セクション切替
 *   - 'scrollProgress'  { progress, section, ... }       … 連続スクロール量 (rAFスロットル済み)
 *
 * セクションごとにカメラの視線（controls.target のみ）・星空の色味・
 * 蛍の濃さ・流れ星の有無を GSAP でゆっくり切り替える。
 * イントロカメラアニメーションが終わるまでイベントは保留する。
 *
 * NOTE: Experience → UI の依存は持たない（DOMイベント経由の一方向連携）。
 */
export default class SceneDirector {
  constructor() {
    this.experience = new Experience()
    this.camera = this.experience.camera
    this.time = this.experience.time
    this.quality = this.experience.quality
    this.debug = this.experience.debug
    this.loadingScreen = this.experience.loadingScreen

    this.currentSection = 'home'
    this.pendingSection = null
    this.introDone = false
    // 直前に適用したセクションが本フォーカス（focus.page）だったか（本を閉じるタイミングの判定に使う）
    this.prevSectionWasBookFocus = false
    // 直前に適用したセクションが TV セクション（tv: true）だったか（TV を消灯するタイミングの判定に使う）
    this.prevSectionWasTv = false

    // スクロールに応じた星空全体のゆっくりしたロール（視差感）
    this.skyRollTarget = 0
    this.skyRollCurrent = 0

    // セクション別の演出スクリプト
    this.sections = {
      home: {
        target: { x: 0, y: 0, z: 0 },
        sky: { brightness: 1.0, tint: '#ffffff', twinkleSpeed: 3.0, nebula: 0.06 },
        fireflies: 0.6,
        shootingStars: true
      },
      about: {
        // 焚き火とAmeへカメラを寄せる（フリーカメラ + Log Camera Pose で決めた固定姿勢）。
        // target はフォーカス失敗時のフォールバック
        target: { x: -2.0, y: -0.4, z: 6.0 },
        sky: { brightness: 0.85, tint: '#ffedd9', twinkleSpeed: 3.0, nebula: 0.06 },
        fireflies: 1.0,
        shootingStars: true,
        focus: {
          pose: {
            cameraPos: { x: -1.511, y: 0.182, z: 20.461 },
            target: { x: -3.984, y: -0.268, z: 6.313 }
          }
        }
      },
      works: {
        // TV へカメラを寄せる（フリーカメラ + Log Camera Pose で決めた固定姿勢）。
        // target はフォーカス失敗時のフォールバック。iframe負荷と重なるため流れ星は止める
        target: { x: 0, y: 0.5, z: -3.0 },
        sky: { brightness: 1.15, tint: '#d9ebff', twinkleSpeed: 3.0, nebula: 0.12 },
        fireflies: 0.3,
        shootingStars: false,
        focus: {
          pose: {
            cameraPos: { x: -2.862, y: -0.671, z: 17.103 },
            target: { x: 3.339, y: -3.16, z: 1.62 }
          }
        },
        tv: true
      },
      history: {
        // 星座を見上げる固定姿勢（Log Camera Pose 由来）。瞬きをゆっくりにして静けさを出す
        target: { x: 1.5, y: 2.0, z: 0 },
        sky: { brightness: 1.1, tint: '#f2f6ff', twinkleSpeed: 1.5, nebula: 0.09 },
        fireflies: 0.5,
        shootingStars: true,
        focus: {
          pose: {
            cameraPos: { x: -3.86, y: 1.086, z: 20.163 },
            target: { x: 3.724, y: 1.303, z: 1.797 }
          }
        }
      },
      activity: {
        // 本の1ページ目（技術書展の見開き）へカメラを寄せる。target はフォーカス失敗時のフォールバック
        target: { x: -2.0, y: -0.8, z: 8.0 },
        sky: { brightness: 0.8, tint: '#ffe9d2', twinkleSpeed: 2.0, nebula: 0.06 },
        fireflies: 0.7,
        shootingStars: true,
        focus: { page: 1, distance: 1.25 }
      },
      'special-thanks': {
        // LICENSES の見開きへ。キーは要素 id（special-thanks）と完全一致させること
        target: { x: -2.0, y: -0.8, z: 8.0 },
        sky: { brightness: 0.75, tint: '#ffe4c8', twinkleSpeed: 1.2, nebula: 0.05 },
        fireflies: 0.8,
        shootingStars: false,
        focus: { page: 2, distance: 1.25 }
      }
    }

    // フォーカス時にリグをカメラ右方向へ平行移動する量。
    // 本を画面左へ寄せ、右側の2Dテキストパネルと共存させる（#debug で調整可）
    this.focusScreenShift = 0.22

    // tween を使い回すためのスクラッチ
    this.tintScratch = new THREE.Color()

    this.setListeners()
    this.setDebug()
  }

  // World の生成タイミングの都合で参照は遅延解決する
  get world() {
    return this.experience.world
  }

  setListeners() {
    document.addEventListener('sectionChanged', (event) => {
      this.onSectionChanged(event.detail.section)
    })

    document.addEventListener('scrollProgress', (event) => {
      this.skyRollTarget = event.detail.progress * 0.12
    })

    // イントロ（1秒遅延 + カメラtargetアニメーション）が終わってから演出を有効化
    this.loadingScreen.on('completed', () => {
      const introSeconds = 1.0 + this.camera.animationState.duration / 1000 + 0.3
      gsap.delayedCall(introSeconds, () => {
        this.introDone = true
        if (this.pendingSection) {
          this.applySection(this.pendingSection)
          this.pendingSection = null
        }
      })
    })

    // reduced-motion が実行中に切り替わったら、現在のセクションへ即時スナップ
    this.quality.on('changed', () => {
      if (this.introDone) {
        this.applySection(this.currentSection)
      }
    })

    this.time.on('tick', () => {
      this.update()
    })
  }

  onSectionChanged(section) {
    if (!this.sections[section]) return
    this.currentSection = section

    if (!this.introDone) {
      this.pendingSection = section
      return
    }
    this.applySection(section)
  }

  applySection(section) {
    const cfg = this.sections[section]
    if (!cfg) return

    const motionScale = this.quality.motionScale

    // 1. カメラ — focus 付きセクションは視点を寄せ、それ以外は視線（target のみ）
    //    focus.pose: 固定姿勢（フリーカメラ + Log Camera Pose の出力をそのまま貼る）
    //    focus.page: 本を開いて見開きへ（姿勢は本の位置から算出）
    const book = this.world?.book

    // 本の開閉は「本フォーカスのセクションかどうか」の履歴で判定する
    //（カメラ状態に依存するとフリーカメラ中に本が開いたままになる。
    //  pose フォーカスの works へ移った時も activity で開いた本を閉じる）
    const isBookFocus = !!(cfg.focus && cfg.focus.page !== undefined)
    if (!isBookFocus && this.prevSectionWasBookFocus) {
      book?.setPage(0)
      book?.setFloatCalm(false)
    }
    this.prevSectionWasBookFocus = isBookFocus

    // TV: works 進入で選択中の作品の動画を点灯、離脱で消灯
    const tv = this.world?.tv
    const isTvSection = !!cfg.tv
    if (isTvSection) {
      tv?.showVideo(tv.currentVideoIndex ?? 0)
    } else if (this.prevSectionWasTv) {
      tv?.turnOff()
    }
    this.prevSectionWasTv = isTvSection

    // フォーカス姿勢の決定
    let pose = null
    if (cfg.focus?.pose) {
      // 固定姿勢モード。画面構図込みで決めた座標なので focusScreenShift は適用しない
      const p = cfg.focus.pose
      pose = {
        cameraPos: new THREE.Vector3(p.cameraPos.x, p.cameraPos.y, p.cameraPos.z),
        lookTarget: new THREE.Vector3(p.target.x, p.target.y, p.target.z)
      }
    } else if (isBookFocus && book) {
      book.setPage(cfg.focus.page)

      pose = book.getFocusPose(cfg.focus.distance)
      // 2Dテキスト（右側）と共存させるため、リグをカメラ右方向へ平行移動して本を画面左へ
      const forward = new THREE.Vector3().subVectors(pose.lookTarget, pose.cameraPos)
      const right = forward.cross(new THREE.Vector3(0, 1, 0)).normalize()
      const shift = right.multiplyScalar(this.focusScreenShift)
      pose.cameraPos.add(shift)
      pose.lookTarget.add(shift)
    }

    if (pose) {
      // スコープ覗き込み中・フリーカメラ中などは false（次のセクション切替で回復）
      const focused = this.camera.focusOn(pose.cameraPos, pose.lookTarget, { duration: 2.2 * motionScale })
      if (isBookFocus) book?.setFloatCalm(focused)
    } else if (this.camera.isFocusMode) {
      this.camera.clearFocus(cfg.target, { duration: 2.0 * motionScale })
    } else {
      this.camera.setSectionTarget(cfg.target, 2.0 * motionScale)
    }

    // 2. 星空の uniforms
    const starrySky = this.world?.StarrySky
    if (starrySky) {
      const uniforms = starrySky.material.uniforms
      const nebulaMax = this.quality.tier === 'low' ? 0.08 : 0.15
      gsap.to(uniforms.uBrightness, { value: cfg.sky.brightness, duration: 2.0 * motionScale, ease: 'power2.inOut', overwrite: true })
      gsap.to(uniforms.uTwinkleSpeed, { value: cfg.sky.twinkleSpeed, duration: 2.0 * motionScale, ease: 'power2.inOut', overwrite: true })
      gsap.to(uniforms.uNebulaIntensity, { value: Math.min(cfg.sky.nebula, nebulaMax), duration: 2.5 * motionScale, ease: 'power2.inOut', overwrite: true })

      this.tintScratch.set(cfg.sky.tint)
      gsap.to(uniforms.uTint.value, { r: this.tintScratch.r, g: this.tintScratch.g, b: this.tintScratch.b, duration: 2.5 * motionScale, ease: 'power2.inOut', overwrite: true })
    }

    // 3. 蛍の濃さ
    const fireflies = this.world?.fireflies
    if (fireflies) {
      gsap.to(fireflies.material.uniforms.uGlobalAlpha, { value: cfg.fireflies, duration: 1.5 * motionScale, ease: 'power2.inOut', overwrite: true })
    }

    // 4. 流れ星の有無
    const shootingStars = this.world?.shootingStars
    if (shootingStars) {
      shootingStars.setEnabled(cfg.shootingStars)
    }
  }

  update() {
    // スクロール量に応じて星空全体をゆっくりロールさせる（reduced-motion 時は無効）
    const starrySky = this.world?.StarrySky
    if (!starrySky || this.quality.reducedMotion) return

    const deltaSeconds = Math.min(this.time.delta, 100) * 0.001
    this.skyRollCurrent += (this.skyRollTarget - this.skyRollCurrent) * Math.min(1, deltaSeconds * 2.0)
    starrySky.mesh.rotation.z = this.skyRollCurrent
  }

  setDebug() {
    if (!this.debug.active) return

    this.debugFolder = this.debug.ui.addFolder('Scene Director')
    const controls = {
      section: 'home',
      forceApply: () => {
        this.introDone = true
        this.applySection(controls.section)
      }
    }
    this.debugFolder.add(controls, 'section', Object.keys(this.sections)).name('Section')
    this.debugFolder.add(controls, 'forceApply').name('Apply Section')
    this.debugFolder.add(this, 'introDone').name('Intro Done').listen()

    // フォーカス演出の調整（変更後は Apply Section で反映）
    this.debugFolder.add(this, 'focusScreenShift').min(-1).max(1).step(0.01).name('Focus Shift')
    const focusTuning = { distance: 1.25 }
    this.debugFolder.add(focusTuning, 'distance').min(0.5).max(4).step(0.05).name('Focus Distance')
      .onChange((value) => {
        Object.values(this.sections).forEach((cfg) => {
          if (cfg.focus) cfg.focus.distance = value
        })
      })
    this.debugFolder.close()
  }
}
